import { BaseService } from '../base/BaseService';

import type { PersonaEvidenceItem } from '@/components/personas/persona-attribution';
import type { Json } from '@/integrations/supabase/types';
import { type ConceptDeliveryIntent, getConceptDisplayLabel } from '@/lib/briefs/concept-contract';
import { conceptTitle } from '@/lib/briefs/concept-title';
import { copyShortfall, isCopyStep } from '@/lib/briefs/copy-shortfall';
import { formatBriefRunFailure } from '@/lib/briefs/run-failure';
import {
  type AdUnitRecord,
  type AdUnitRow,
  buildAdUnitRecord,
} from '@/services/data/AdUnitsService';
import { readPersonaEvidence } from '@/services/data/BrandAudienceService';

export interface BriefSummary {
  id: string;
  name: string;
  objective: string | null;
  status: string;
  platforms: string[];
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  brandKitId?: string | null;
}

export interface BriefProductReference {
  id: string;
  title: string;
  imageUrl: string | null;
  position: number;
}

export interface BriefProvenanceStep {
  id: string;
  stepKey: string | null;
  label: string;
  position: number;
  status: string;
  finding: string | null;
  findingKind: string | null;
  detail: Record<string, unknown> | null;
}

export interface BriefProvenance {
  latestRun: {
    id: string;
    status: string;
    mode: string;
    sourceKind: string;
    sourceUrl: string | null;
    sourceTopic: string | null;
    conceptCount: number;
    errorMessage: string | null;
    createdAt: string;
    updatedAt: string;
    steps: BriefProvenanceStep[];
  } | null;
  creationCommand: BriefCreationCommandState | null;
  products: BriefProductReference[];
}

/**
 * The durable command ledger is the authority for an Auto Brief's dispatch
 * state. A queued run alone cannot prove that the external workflow start was
 * accepted, especially after a lost response.
 */
export type BriefCreationGenerationState =
  'pending' | 'starting' | 'enqueue_unknown' | 'enqueued' | 'superseded' | 'failed';

export interface BriefCreationCommandState {
  commandId: string;
  mode: 'manual' | 'auto';
  idempotencyKey: string;
  dispatchState: 'not_required' | 'pending' | 'enqueue_unknown' | 'enqueued' | 'failed';
  workflowRunId: string | null;
  lastDispatchError: string | null;
  latestGeneration: {
    generation: number;
    state: BriefCreationGenerationState;
    workflowRunId: string | null;
    executionWorkflowRunId: string | null;
    reconciliationNote: string | null;
    lastError: string | null;
  } | null;
}

export interface BriefConceptCopyRevision {
  id: string;
  status: string;
  updatedAt: string;
  headline: string | null;
  primaryText: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
}

export type BriefAdUnitRecord = AdUnitRecord & {
  projectTaskId: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
};

export interface BriefConceptMediaRevision {
  id: string;
  kind: string;
  filename: string | null;
  url: string | null;
  placement: string | null;
  position: number;
  createdAt: string;
}

export interface BriefConceptSummary {
  id: string;
  sourceRunId: string | null;
  audienceId: string | null;
  title: string;
  description: string | null;
  cta: string | null;
  format: string | null;
  position: number;
  status: string;
  updatedAt: string | null;
  intents: Array<{ intent: ConceptDeliveryIntent; platform: string | null; position: number }>;
  displayLabel: 'Ad concept' | 'Post concept' | 'Creative concept';
  currentCopy: BriefConceptCopyRevision | null;
  adUnits: BriefAdUnitRecord[];
  currentMedia: BriefConceptMediaRevision[];
  assignmentIssue: boolean;
}

export interface BriefPersona {
  id: string;
  name: string;
  who: string;
  desire: string | null;
  painLanguage: string | null;
  objection: string | null;
  status: string;
  source: string;
  targetingNotes: string | null;
  position: number;
  concepts: BriefConceptSummary[];
  /**
   * Already on the wire, and has been since the snapshot RPC was fixed:
   * `20260810112633_fix_brief_workspace_intent_snapshot_shape.sql:67` returns
   * `to_jsonb(audience)`, i.e. the WHOLE `brand_audiences` row. Only the inline
   * type below omitted it, so the Brief page could not show the source
   * sentences its Personas were derived from despite already holding them.
   */
  evidence: PersonaEvidenceItem[];
}

export interface BriefWorkspaceBlocker {
  code: string;
  scope: 'brief' | 'concept' | 'provenance';
  message: string;
  conceptId?: string;
}

export interface BriefCapabilities {
  canEdit: boolean;
  canArchive: boolean;
  canRestore: boolean;
  canAddPersona: boolean;
  /** A Concept must begin under an attached Persona; archived Briefs are read-only. */
  canCreateConcept: boolean;
  canUpload: false;
  canLaunch: false;
}

export interface BriefWorkspace {
  brief: BriefSummary;
  provenance: BriefProvenance;
  personas: BriefPersona[];
  unassignedConcepts: BriefConceptSummary[];
  blockers: BriefWorkspaceBlocker[];
  capabilities: BriefCapabilities;
  version: number;
}

type BriefRow = {
  id: string;
  name: string;
  goal: string | null;
  brand_kit_id?: string | null;
  status: string;
  platforms: Json | null;
  archived_at: string | null;
  version: number;
  created_at: string | null;
  updated_at: string | null;
  user_id: string;
};

type PersonaRow = {
  audience_id: string;
  position: number;
  targeting_notes: string | null;
  brand_audiences: unknown;
};

type ConceptRow = {
  id: string;
  source_run_id: string | null;
  audience_id: string | null;
  hook: string | null;
  topic: string | null;
  summary: string | null;
  rationale: string | null;
  cta: string | null;
  format: string | null;
  position: number | null;
  status: string;
  updated_at: string | null;
  project_task_delivery_intents: unknown;
  ad_units: unknown;
  project_files: unknown;
};

type ProductRow = {
  product_id: string;
  position: number;
  shopify_products: unknown;
};

type RunRow = {
  id: string;
  status: string;
  mode: string;
  source_kind: string;
  source_url: string | null;
  source_topic: string | null;
  concept_count: number;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  brief_run_steps: unknown;
};

type CreationCommandRow = {
  id: string;
  mode: string;
  idempotency_key: string;
  dispatch_state: string;
  workflow_run_id: string | null;
  last_dispatch_error: string | null;
  latest_generation: unknown;
};

function oneRelation<T>(value: unknown): T | null {
  if (Array.isArray(value)) return (value[0] as T | undefined) ?? null;
  return (value as T | null) ?? null;
}

function manyRelation<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : value ? [value as T] : [];
}

function stringArray(value: Json | null): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function mapBrief(row: BriefRow): BriefSummary {
  return {
    id: row.id,
    name: row.name,
    objective: row.goal?.trim() || null,
    status: row.status,
    platforms: stringArray(row.platforms),
    archivedAt: row.archived_at,
    version: row.version,
    brandKitId: row.brand_kit_id ?? null,
    createdAt: row.created_at ?? row.updated_at ?? new Date(0).toISOString(),
    updatedAt: row.updated_at ?? row.created_at ?? new Date(0).toISOString(),
  };
}

function mapConcept(row: ConceptRow): BriefConceptSummary {
  const intents = manyRelation<{ intent: string; platform: string | null; position: number }>(
    row.project_task_delivery_intents,
  )
    .filter(
      (
        item,
      ): item is { intent: ConceptDeliveryIntent; platform: string | null; position: number } =>
        item.intent === 'organic' || item.intent === 'paid',
    )
    .sort((left, right) => left.position - right.position);

  const copyRows = manyRelation<{
    id: string;
    status: string;
    brief_id: string | null;
    project_task_id: string | null;
    product: string;
    ad_platform: string;
    offer_class: string;
    copy: Json;
    grounding: Json;
    warnings: Json;
    reviewed_by: string | null;
    reviewed_at: string | null;
    review_note: string | null;
    created_at: string;
  }>(row.ad_units).sort(
    (left, right) =>
      right.created_at.localeCompare(left.created_at) || right.id.localeCompare(left.id),
  );

  // `adUnits` (below) feeds the Brief-page review UI (`BriefConceptAdUnits`),
  // which must show the same "Option 1" row the Concept workbench does —
  // ascending created_at, ascending id tiebreak, the exact convention
  // `ConceptCopySection.byCreationThenId` defines and `ConceptPreviewPane`
  // duplicates deliberately to match it "bit-for-bit". `copyRows` above is
  // sorted the OPPOSITE way (newest first) on purpose, for `currentCopy`'s own
  // "most recent revision" need — reusing that same array for `adUnits`
  // inverted the tiebreak too and made the Brief page's default ad card show
  // a genuinely different variant than every other surface, on batch-generated
  // siblings that share one timestamp (found live: a real Concept whose 3
  // same-millisecond ad_units rows rendered "Zero liners. Zero chafing." as
  // Option 1 in the workbench but "7-inch shorts without liners" here).
  const orderedCopyRows = [...copyRows].sort(
    (left, right) =>
      left.created_at.localeCompare(right.created_at) || left.id.localeCompare(right.id),
  );

  const copySummary = (copy: unknown): { headline: string | null; primaryText: string | null } => {
    if (!copy || typeof copy !== 'object' || Array.isArray(copy)) {
      return { headline: null, primaryText: null };
    }
    const record = copy as { headlines?: unknown; primaryTexts?: unknown; description?: unknown };
    const headline = Array.isArray(record.headlines)
      ? record.headlines
          .find(
            (item): item is { text: string } =>
              Boolean(item) &&
              typeof item === 'object' &&
              typeof (item as { text?: unknown }).text === 'string',
          )
          ?.text.trim() || null
      : null;
    const primaryText = Array.isArray(record.primaryTexts)
      ? record.primaryTexts
          .find(
            (item): item is { text: string } =>
              Boolean(item) &&
              typeof item === 'object' &&
              typeof (item as { text?: unknown }).text === 'string',
          )
          ?.text.trim() || null
      : typeof record.description === 'string'
        ? record.description.trim() || null
        : null;
    return { headline, primaryText };
  };

  const mediaBySlot = new Map<string, BriefConceptMediaRevision>();
  for (const file of manyRelation<{
    id: string;
    kind: string;
    filename: string | null;
    url: string | null;
    ad_placement: string | null;
    creative_variant_position: number;
    created_at: string;
  }>(row.project_files).sort((left, right) => right.created_at.localeCompare(left.created_at))) {
    // Planning references belong to the Concept editor, not its produced-media
    // inventory. Counting them as media would claim an output before anything
    // has actually been created or uploaded.
    if (file.kind === 'reference-url') continue;
    // `creative_variant` mixes old presentation formats and placements. A
    // task-bound asset is selected by its provider placement and ordinal.
    const key = `${file.ad_placement ?? 'general'}:${file.creative_variant_position}`;
    if (mediaBySlot.has(key)) continue;
    mediaBySlot.set(key, {
      id: file.id,
      kind: file.kind,
      filename: file.filename,
      url: file.url,
      placement: file.ad_placement,
      position: file.creative_variant_position,
      createdAt: file.created_at,
    });
  }

  return {
    id: row.id,
    sourceRunId: row.source_run_id ?? null,
    audienceId: row.audience_id,
    title: conceptTitle(row),
    description: row.summary?.trim() || row.rationale?.trim() || null,
    cta: row.cta?.trim() || null,
    format: row.format?.trim() || null,
    position: row.position ?? 0,
    status: row.status,
    updatedAt: row.updated_at,
    intents,
    displayLabel: getConceptDisplayLabel(intents.map((intent) => intent.intent)),
    currentCopy: copyRows[0]
      ? {
          id: copyRows[0].id,
          status: copyRows[0].status,
          updatedAt: copyRows[0].created_at,
          ...copySummary(copyRows[0].copy),
          reviewedAt: copyRows[0].reviewed_at,
          reviewNote: copyRows[0].review_note,
        }
      : null,
    // Built through the shared `buildAdUnitRecord`, not hand-mapped. The
    // hand-mapped version cast `copy` straight to the Meta shape regardless of
    // platform and never read the `warnings` column at all — two of the three
    // bugs this seam exists to prevent.
    adUnits: orderedCopyRows.map((unit): BriefAdUnitRecord => ({
      ...buildAdUnitRecord(unit as unknown as AdUnitRow),
      projectTaskId: unit.project_task_id,
    })) as BriefAdUnitRecord[],
    currentMedia: Array.from(mediaBySlot.values()).sort(
      (left, right) => left.position - right.position,
    ),
    assignmentIssue: false,
  };
}

function mapProduct(row: ProductRow): BriefProductReference {
  const product = oneRelation<{ title: string; images: Json | null }>(row.shopify_products);
  const images = Array.isArray(product?.images) ? product.images : [];
  const firstImage = images[0];
  const imageUrl =
    firstImage && typeof firstImage === 'object' && !Array.isArray(firstImage)
      ? ((firstImage as Record<string, Json | undefined>).url as string | undefined)
      : undefined;

  return {
    id: row.product_id,
    title: product?.title ?? 'Unavailable product',
    imageUrl: imageUrl ?? null,
    position: row.position,
  };
}

function mapRun(row: RunRow | null): BriefProvenance['latestRun'] {
  if (!row) return null;

  return {
    id: row.id,
    status: row.status,
    mode: row.mode,
    sourceKind: row.source_kind,
    sourceUrl: row.source_url,
    sourceTopic: row.source_topic,
    conceptCount: row.concept_count,
    errorMessage: row.error_message,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    steps: manyRelation<{
      id: string;
      step_key?: string | null;
      detail?: Record<string, unknown> | null;
      label: string;
      position: number;
      status: string;
      finding: string | null;
      finding_kind: string | null;
    }>(row.brief_run_steps)
      .map((step) => ({
        id: step.id,
        stepKey: step.step_key ?? null,
        label: step.label,
        position: step.position,
        status: step.status,
        finding: step.finding,
        findingKind: step.finding_kind,
        detail: step.detail ?? null,
      }))
      .sort((left, right) => left.position - right.position),
  };
}

function mapCreationCommand(value: unknown): BriefCreationCommandState | null {
  const row = oneRelation<CreationCommandRow>(value);
  if (
    !row ||
    typeof row.id !== 'string' ||
    (row.mode !== 'manual' && row.mode !== 'auto') ||
    typeof row.idempotency_key !== 'string' ||
    !['not_required', 'pending', 'enqueue_unknown', 'enqueued', 'failed'].includes(
      row.dispatch_state,
    )
  ) {
    return null;
  }

  const generation = oneRelation<{
    generation: number;
    state: string;
    workflow_run_id: string | null;
    execution_workflow_run_id: string | null;
    reconciliation_note: string | null;
    last_error: string | null;
  }>(row.latest_generation);
  const latestGeneration =
    generation &&
    typeof generation.generation === 'number' &&
    ['pending', 'starting', 'enqueue_unknown', 'enqueued', 'superseded', 'failed'].includes(
      generation.state,
    )
      ? {
          generation: generation.generation,
          state: generation.state as BriefCreationGenerationState,
          workflowRunId: generation.workflow_run_id,
          executionWorkflowRunId: generation.execution_workflow_run_id,
          reconciliationNote: generation.reconciliation_note,
          lastError: generation.last_error,
        }
      : null;

  return {
    commandId: row.id,
    mode: row.mode,
    idempotencyKey: row.idempotency_key,
    dispatchState: row.dispatch_state as BriefCreationCommandState['dispatchState'],
    workflowRunId: row.workflow_run_id,
    lastDispatchError: row.last_dispatch_error,
    latestGeneration,
  };
}

export class BriefWorkspaceService extends BaseService {
  async getWorkspace(briefId: string): Promise<BriefWorkspace> {
    await this.ensureAuthenticatedOwner();
    const { data: snapshotData, error: snapshotError } = await this.client.rpc(
      'get_brief_workspace',
      { p_brief_id: briefId },
    );
    if (snapshotError) throw snapshotError;

    const snapshot = snapshotData as unknown as {
      brief: BriefRow | null;
      personas: PersonaRow[];
      concepts: ConceptRow[];
      products: ProductRow[];
      latest_run: RunRow | null;
      creation_command: CreationCommandRow | null;
    } | null;
    if (!snapshot?.brief) throw new Error('Brief not found');

    const brief = mapBrief(snapshot.brief);
    const concepts = (snapshot.concepts ?? []).map(mapConcept);
    const conceptByPersona = new Map<string, BriefConceptSummary[]>();
    const unassignedConcepts: BriefConceptSummary[] = [];
    const blockers: BriefWorkspaceBlocker[] = [];

    for (const concept of concepts) {
      if (!concept.audienceId) {
        unassignedConcepts.push(concept);
        continue;
      }
      const group = conceptByPersona.get(concept.audienceId) ?? [];
      group.push(concept);
      conceptByPersona.set(concept.audienceId, group);
    }

    const personas = (snapshot.personas ?? []).map((row) => {
      const persona = oneRelation<{
        id: string;
        name: string;
        who: string;
        desire: string | null;
        pain_language: string | null;
        objection: string | null;
        status: string;
        source: string;
        evidence: unknown;
      }>(row.brand_audiences);
      if (!persona) throw new Error('Attached Persona could not be loaded');

      const groupedConcepts = conceptByPersona.get(row.audience_id) ?? [];
      conceptByPersona.delete(row.audience_id);
      return {
        id: persona.id,
        name: persona.name,
        who: persona.who,
        desire: persona.desire,
        painLanguage: persona.pain_language,
        objection: persona.objection,
        status: persona.status,
        source: persona.source,
        targetingNotes: row.targeting_notes,
        position: row.position,
        concepts: groupedConcepts,
        evidence: readPersonaEvidence(persona.evidence),
      };
    });

    for (const danglingConcepts of conceptByPersona.values()) {
      for (const concept of danglingConcepts) {
        concept.assignmentIssue = true;
        unassignedConcepts.push(concept);
        blockers.push({
          code: 'concept_persona_unavailable',
          scope: 'concept',
          conceptId: concept.id,
          message: `${concept.title} references a Persona that is not attached to this Brief.`,
        });
      }
    }

    const latestRun = mapRun(snapshot.latest_run ?? null);
    const creationCommand = mapCreationCommand(snapshot.creation_command ?? null);
    if (latestRun?.status === 'succeeded') {
      const persistedForLatestRun = concepts.filter(
        (concept) => concept.sourceRunId === latestRun.id,
      ).length;
      const legacyUnprovenancedConcepts = concepts.filter(
        (concept) => concept.sourceRunId === null,
      ).length;
      if (persistedForLatestRun < latestRun.conceptCount) {
        const legacyContext =
          legacyUnprovenancedConcepts > 0
            ? `; ${legacyUnprovenancedConcepts} legacy unprovenanced ${
                legacyUnprovenancedConcepts === 1 ? 'Concept is' : 'Concepts are'
              } also attached to this Brief`
            : '';
        blockers.push({
          code: 'brief_run_concept_underfill',
          scope: 'provenance',
          message: `${persistedForLatestRun} of ${latestRun.conceptCount} requested Concepts are provenanced to this run${legacyContext}.`,
        });
      }
    }
    if (
      creationCommand?.dispatchState === 'enqueue_unknown' ||
      ['starting', 'enqueue_unknown'].includes(creationCommand?.latestGeneration?.state ?? '')
    ) {
      blockers.push({
        code: 'brief_dispatch_unconfirmed',
        scope: 'provenance',
        message:
          'Mora cannot confirm that the original workflow started. Check its status before choosing a replacement generation.',
      });
    }
    if (creationCommand?.dispatchState === 'failed') {
      blockers.push({
        code: 'brief_dispatch_failed',
        scope: 'provenance',
        message:
          creationCommand.lastDispatchError?.trim() || 'Mora could not queue the Brief workflow.',
      });
    }
    if (latestRun?.status === 'failed') {
      blockers.push({
        code: 'research_run_failed',
        scope: 'brief',
        message: formatBriefRunFailure(
          latestRun.errorMessage,
          latestRun.steps.map((step) => ({ label: step.label, status: step.status })),
        ).text,
      });
    }
    const copyGapAtRun = latestRun?.status === 'succeeded' ? copyShortfall(latestRun.steps) : null;
    const copyGap =
      latestRun?.status === 'succeeded'
        ? copyShortfall(latestRun.steps, {
            conceptsWithCopy: new Set(
              concepts.filter((concept) => concept.adUnits.length > 0).map((concept) => concept.id),
            ),
          })
        : null;
    if (copyGap) {
      blockers.push({ code: 'copy_not_written', scope: 'brief', message: copyGap.sentence });
    }
    for (const step of latestRun?.steps ?? []) {
      if (copyGapAtRun && isCopyStep(step)) continue;
      if (!step.findingKind || !['partial', 'warning', 'error'].includes(step.findingKind))
        continue;
      blockers.push({
        code: 'research_step_partial',
        scope: 'provenance',
        message: `${step.label}: ${step.finding?.trim() || `The research step is ${step.findingKind}.`}`,
      });
    }
    for (const concept of concepts) {
      if (concept.status !== 'failed') continue;
      blockers.push({
        code: 'concept_failed',
        scope: 'concept',
        conceptId: concept.id,
        message: `${concept.title} has a failed operation.`,
      });
    }

    return {
      brief,
      provenance: {
        latestRun,
        creationCommand,
        products: (snapshot.products ?? []).map(mapProduct),
      },
      personas,
      unassignedConcepts: unassignedConcepts.sort((left, right) => left.position - right.position),
      blockers,
      capabilities: {
        canEdit: !brief.archivedAt,
        canArchive: !brief.archivedAt,
        canRestore: Boolean(brief.archivedAt),
        canAddPersona: !brief.archivedAt,
        canCreateConcept: !brief.archivedAt,
        canUpload: false,
        canLaunch: false,
      },
      version: brief.version,
    };
  }

  async updateIdentity(input: {
    briefId: string;
    name: string;
    objective: string | null;
    expectedVersion: number;
  }): Promise<BriefWorkspace> {
    const ownerId = await this.ensureAuthenticatedOwner();
    const name = input.name.trim();
    if (!name) throw new Error('Brief name is required');

    const { data, error } = await this.client
      .from('projects')
      .update({
        name,
        goal: input.objective?.trim() || null,
        version: input.expectedVersion + 1,
        updated_at: new Date().toISOString(),
      })
      .eq('id', input.briefId)
      .eq('kind', 'brief')
      .eq('user_id', ownerId)
      .is('archived_at', null)
      .eq('version', input.expectedVersion)
      .select('id')
      .maybeSingle();

    if (error) throw error;
    if (!data) {
      const conflict = new Error('This Brief changed elsewhere. Reload it and try again.');
      Object.assign(conflict, { code: 'brief_version_conflict' });
      throw conflict;
    }

    return this.getWorkspace(input.briefId);
  }

  async setArchived(input: {
    briefId: string;
    archived: boolean;
    expectedVersion: number;
  }): Promise<BriefWorkspace> {
    const ownerId = await this.ensureAuthenticatedOwner();
    let update = this.client
      .from('projects')
      .update({
        archived_at: input.archived ? new Date().toISOString() : null,
        version: input.expectedVersion + 1,
        updated_at: new Date().toISOString(),
      })
      .eq('id', input.briefId)
      .eq('kind', 'brief')
      .eq('user_id', ownerId)
      .eq('version', input.expectedVersion);
    update = input.archived
      ? update.is('archived_at', null)
      : update.not('archived_at', 'is', null);
    const { data, error } = await update.select('id').maybeSingle();

    if (error) throw error;
    if (!data) {
      const conflict = new Error('This Brief changed elsewhere. Reload it and try again.');
      Object.assign(conflict, { code: 'brief_version_conflict' });
      throw conflict;
    }

    return this.getWorkspace(input.briefId);
  }

  async reviewAdUnit(input: {
    adUnitId: string;
    status: string;
    reviewNote?: string | null;
  }): Promise<void> {
    await this.ensureAuthenticatedOwner();
    const { error } = await this.client.rpc('transition_ad_unit_review', {
      p_ad_unit_id: input.adUnitId,
      p_status: input.status,
      p_review_note: input.reviewNote ?? undefined,
    });
    if (error) throw error;
  }
}

export const briefWorkspaceService = new BriefWorkspaceService();
